import { Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../_base-shared/contracts/laravel-response.interface';
import { Case } from '../../../../../_base-shared/models/Case/Case';
import { CaseCreditor, CaseCreditorPivot } from '../../../../../_base-shared/models/Case/CaseCreditor';
import { CaseCreditorStatusChange } from '../../../../../_base-shared/models/Case/CaseCreditorStatusChange';
import { Creditor } from '../../../../../_base-shared/models/Entity/Creditor';
import { MainBaseApiService } from '../../_shared/services/main-base-api.service';
import { Observable } from 'rxjs';
import { CaseCreditorStatus } from '../../../../../_base-shared/models/Status/CaseCreditorStatus';
import { CasePublicDebt } from '../../../../../_base-shared/models/Case/CasePublicDebt';

type SupportedEmails = 'SettlementDemand' | 'ExtrajudicialNotice' | 'SendInvoiceToCreditorNotification' | 'NewContract';
type SupportedDocTypes = 'settlement-demand' | 'extrajudicial-notice' | 'creditor-invoice';

@Injectable({
  providedIn: 'root',
})
export class CaseCreditorService extends MainBaseApiService {
  public index(data: any): Observable<LaravelResourceResponse<Array<CaseCreditor>>> {
    return this.http.get<LaravelResourceResponse<Array<CaseCreditor>>>(`${this.apiUrl}/case-creditors`, {params: data})
      .pipe(catchError(response => this.handleError(response)));
  }

  public export(filters): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(this.apiUrl + '/case-creditors/export', {params: filters})
      .pipe(catchError(response => this.handleError(response)));
  }

  public indexCaseCreditors(caseId: number, params) {
    return this.http.get<LaravelResourceResponse<{
      secured: Array<Creditor>, unsecured: Array<CaseCreditorPivot>,
      claim: Array<Creditor>, excluded: Array<Creditor>
    }>>(
      this.apiUrl + '/cases/' + caseId + '/creditors', {params},
    ).pipe(catchError(response => this.handleError(response)));
  }

  public indexCaseCreditorsV2(caseId: number, relations: Array<string> = [], requestData = {}): Observable<LaravelResourceResponse<Array<CaseCreditor>>> {
    const params = MainBaseApiService.convertFiltersForRequest({...requestData, ...{with: relations}}, 'get');
    return this.http.get<LaravelResourceResponse<Array<CaseCreditor>>>(
      this.apiUrl + '/cases/' + caseId + '/case-creditors', {params},
    ).pipe(catchError(response => this.handleError(response)));
  }

  public upsert(caseId: number, requestData) {
    return this.http.put<LaravelResourceResponse<Case>>(
      this.apiUrl + '/cases/' + caseId + '/creditors', requestData,
    ).pipe(catchError(response => this.handleError(response)));
  }

  public getCaseCreditorStatuses(data: any = {}, relations: Array<string> = [], appends: Array<string> = [])
    : Observable<LaravelResourceResponse> {
    data.with    = relations;
    data.append  = appends;
    const params = MainBaseApiService.convertFiltersForRequest(data, 'get');
    return this.http.get<LaravelResourceResponse<Array<CaseCreditorStatus>>>(
      this.apiUrl + '/case-creditor-statuses', {params}
    ).pipe(catchError(response => this.handleError(response)));
  }

  public acceptRejectPlan(data: any) {
    return this.http.post<LaravelResourceResponse>(
      this.apiUrl + '/cases/accept-reject-plan',
      data,
    ).pipe(catchError(response => this.handleError(response)));
  }

  public acceptSettlementAmount(caseId: number, creditorId: number, data: any) {
    return this.http.post<LaravelResourceResponse>(
      this.apiUrl + '/cases/' + caseId + '/creditors/' + creditorId + '/settlement',
      data,
    ).pipe(catchError(response => this.handleError(response)));
  }

  public getCaseCreditors(caseId: number, data: any = {}, relations: Array<string> = []): Observable<LaravelResourceResponse<Array<CaseCreditor>>> {
    data.with    = relations
    const params = MainBaseApiService.convertFiltersForRequest(data, 'get');
    return this.http.get<LaravelResourceResponse<Array<CaseCreditor>>>(
      this.apiUrl + '/cases/' + caseId + '/case-creditors', {params},
    ).pipe(catchError(response => this.handleError(response)));
  }

  public showCaseCreditor(
    caseId: number,
    caseCreditorId: number,
    relations: Array<string> = [],
    requestData: any         = {}): Observable<LaravelResourceResponse<CaseCreditor>> {
    const params = MainBaseApiService.convertFiltersForRequest({...requestData, ...{with: relations}}, 'get');
    return this.http.get<LaravelResourceResponse<CaseCreditor>>(
      this.apiUrl + '/cases/' + caseId + '/case-creditors/' + caseCreditorId, {params},
    ).pipe(catchError(response => this.handleError(response)));
  }

  public getCaseCreditorStatusLogs(caseId: number, params = {}): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse<Array<CaseCreditorStatusChange>>>(
      this.apiUrl + '/cases/' + caseId + '/case-creditor-status-changes', {params})
      .pipe(catchError(response => this.handleError(response)));
  }

  public indexStatusChanges(caseId: number, caseCreditorId: number, params = {}): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(this.apiUrl + '/cases/' + caseId + '/case-creditors/' + caseCreditorId + '/status-changes', {params})
      .pipe(catchError(response => this.handleError(response)));
  }

  public caseCreditorCSVUpdate(formData): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/case-creditors/csv-bulk-update', formData)
      .pipe(catchError(response => this.handleError(response)));
  }

  public updateCaseCreditor(caseId: number, caseCreditorId: number, data: any): Observable<LaravelResourceResponse> {
    return this.http.patch<LaravelResourceResponse>(this.apiUrl + '/cases/' + caseId + '/case-creditors/' + caseCreditorId,
      data)
      .pipe(catchError(response => this.handleError(response)));
  }

  public bulkUpdateCaseCreditors(data: any): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/case-creditors/bulk-update',
      data)
      .pipe(catchError(response => this.handleError(response)));
  }

  public indexCasePublicDebts(caseId: number, relations: Array<string> = [], requestData = {}): Observable<LaravelResourceResponse<Array<CasePublicDebt>>> {
    const params = MainBaseApiService.convertFiltersForRequest({...requestData, ...{with: relations}}, 'get');
    return this.http.get<LaravelResourceResponse<Array<CasePublicDebt>>>(
      this.apiUrl + '/cases/' + caseId + '/public-debts', {params},
    ).pipe(catchError(response => this.handleError(response)));
  }
}
